import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Mail, Phone, Smartphone, Pencil, FileText, Wrench, Cpu } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  getCompanies,
  getCompanyContacts,
  setContactAccountRole,
  removeContactAccountRole,
  type ContactRole,
} from '@/features/crm/api/crmApi';
import type { Contact } from '@/features/crm/types';
import { useAccountContactRoles } from '../hooks/useSubscriptionManagers';
import type { ContactRoleEntry } from '../api';

const ROLES = [
  {
    short: 'CM',
    label: 'Contractual',
    Icon: FileText,
    roleKey: 'contractual_manager' as const,
  },
  {
    short: 'Tech',
    label: 'Technical',
    Icon: Wrench,
    roleKey: 'technical_manager' as const,
  },
  {
    short: 'IT',
    label: 'IT',
    Icon: Cpu,
    roleKey: 'it_manager' as const,
  },
] as const;

type RoleDef = (typeof ROLES)[number];

function contactName(c: Pick<Contact, 'first_name' | 'last_name' | 'email'>): string {
  const name = [c.first_name, c.last_name].filter(Boolean).join(' ').trim();
  return name || c.email || 'Unknown';
}

function contactRoleEntryName(c: ContactRoleEntry): string {
  const name = [c.first_name, c.last_name].filter(Boolean).join(' ').trim();
  return name || c.email || 'Unknown';
}

function ContactCard({ contact }: { contact: ContactRoleEntry }) {
  const name = contactRoleEntryName(contact);
  return (
    <div className="min-w-0 flex-1">
      <div className="flex items-baseline gap-1.5">
        <span className="truncate text-sm font-medium text-foreground">{name}</span>
        {contact.title && (
          <span className="truncate text-[11px] text-muted-foreground">· {contact.title}</span>
        )}
      </div>
      <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted-foreground">
        {contact.email && (
          <a
            href={`mailto:${contact.email}`}
            className="inline-flex items-center gap-1 hover:text-atek-blue"
            title={contact.email}
          >
            <Mail className="h-3 w-3" />
            <span className="truncate max-w-[160px]">{contact.email}</span>
          </a>
        )}
        {contact.phone && (
          <a
            href={`tel:${contact.phone}`}
            className="inline-flex items-center gap-1 hover:text-atek-blue"
            title={`Office: ${contact.phone}`}
          >
            <Phone className="h-3 w-3" />
            <span>{contact.phone}</span>
          </a>
        )}
        {contact.mobile_phone && (
          <a
            href={`tel:${contact.mobile_phone}`}
            className="inline-flex items-center gap-1 hover:text-atek-blue"
            title={`Mobile: ${contact.mobile_phone}`}
          >
            <Smartphone className="h-3 w-3" />
            <span>{contact.mobile_phone}</span>
          </a>
        )}
      </div>
    </div>
  );
}

function RolePicker({
  role,
  contacts,
  selectedContactId,
  onSelect,
  disabled,
}: {
  role: RoleDef;
  contacts: Contact[];
  selectedContactId: string | null;
  onSelect: (contactId: string | null) => void;
  disabled: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  const filtered = contacts.filter((c) => {
    if (!search) return true;
    return contactName(c).toLowerCase().includes(search.toLowerCase());
  });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 shrink-0 text-muted-foreground hover:text-foreground"
          disabled={disabled}
          aria-label={`Edit ${role.label} Manager`}
        >
          <Pencil className="h-3.5 w-3.5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="end">
        <div className="border-b p-2">
          <Input
            placeholder="Filter contacts..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-7 text-xs"
          />
        </div>
        <div className="max-h-56 overflow-y-auto p-1">
          <button
            type="button"
            className={`w-full text-left px-2 py-1.5 text-xs rounded hover:bg-accent ${
              !selectedContactId ? 'font-medium' : 'text-muted-foreground'
            }`}
            onClick={() => {
              onSelect(null);
              setOpen(false);
            }}
          >
            (none)
          </button>
          {filtered.map((c) => {
            const isSelected = c.id === selectedContactId;
            return (
              <button
                type="button"
                key={c.id}
                className={`w-full text-left px-2 py-1.5 text-xs rounded hover:bg-accent ${
                  isSelected ? 'bg-accent font-medium' : ''
                }`}
                onClick={() => {
                  onSelect(c.id);
                  setOpen(false);
                }}
              >
                <div className="truncate">{contactName(c)}</div>
                {c.title && (
                  <div className="truncate text-[10px] text-muted-foreground">{c.title}</div>
                )}
              </button>
            );
          })}
          {filtered.length === 0 && (
            <div className="px-2 py-3 text-xs text-muted-foreground text-center">
              {contacts.length === 0 ? 'No contacts on linked company' : 'No matching contacts'}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * KeyContactsPanel variant that lists CRM company contacts (not platform usergroup members).
 *
 * Lookup chain:
 *   1. Find the CRM company linked to this account (companies.account_id = accountId).
 *   2. Fetch contacts where company_id = that company.
 *   3. Display current CM / TM / IT from contact_roles (canonical source).
 *   4. Mutations write directly to contact_roles via /v4/crm/contacts/:id/account-roles.
 */
export function CompanyKeyContactsPanel({ accountId }: { accountId: string }) {
  const queryClient = useQueryClient();
  const { data: contactRoles } = useAccountContactRoles(accountId);

  const { data: companiesResult } = useQuery({
    queryKey: ['crm-companies-by-account', accountId],
    queryFn: () => getCompanies({ accountId }, 0, 1),
    enabled: !!accountId,
    staleTime: 5 * 60 * 1000,
  });
  const linkedCompany = companiesResult?.items?.[0] ?? null;

  const { data: contactsResult } = useQuery({
    queryKey: ['crm-company-contacts', linkedCompany?.id],
    queryFn: () => getCompanyContacts(linkedCompany!.id, 0, 100),
    enabled: !!linkedCompany?.id,
    staleTime: 5 * 60 * 1000,
  });
  const contacts = useMemo(() => contactsResult?.items ?? [], [contactsResult]);

  const assignMutation = useMutation({
    mutationFn: async (args: {
      role: ContactRole;
      previousContactId: string | null;
      newContactId: string | null;
    }) => {
      if (args.previousContactId && args.previousContactId !== args.newContactId) {
        await removeContactAccountRole(args.previousContactId, accountId, args.role);
      }
      if (args.newContactId) {
        await setContactAccountRole(args.newContactId, { accountId, role: args.role });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-contact-roles', accountId] });
      queryClient.invalidateQueries({ queryKey: ['account-dashboard', accountId] });
      queryClient.invalidateQueries({ queryKey: ['accounts-table'] });
      toast.success('Role updated');
    },
    onError: (error) => {
      const msg = error instanceof Error ? error.message : 'Unknown error';
      toast.error('Failed to update role', { description: msg });
    },
  });

  return (
    <Card className="w-full md:w-[360px]">
      <CardContent className="p-3 space-y-2.5">
        {!linkedCompany && (
          <div className="text-xs text-muted-foreground italic">
            No CRM company linked to this account.
          </div>
        )}
        {ROLES.map((role) => {
          const assigned = contactRoles?.[role.roleKey] ?? [];
          const primary = assigned[0] || null;
          const extraCount = Math.max(0, assigned.length - 1);

          // Match primary contact_role entry to a CRM contact by email so the
          // picker can highlight the current selection.
          const selectedContact = primary?.email
            ? contacts.find((c) => c.email && c.email.toLowerCase() === primary.email?.toLowerCase()) ?? null
            : null;

          return (
            <div key={role.roleKey} className="flex items-start gap-2">
              <div className="mt-0.5 flex h-5 w-12 shrink-0 items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                <role.Icon className="h-3 w-3" />
                <span>{role.short}</span>
              </div>
              {primary ? (
                <div className="min-w-0 flex-1">
                  <ContactCard contact={primary} />
                  {extraCount > 0 && (
                    <div className="mt-1 text-[10px] text-muted-foreground italic">
                      +{extraCount} more
                    </div>
                  )}
                </div>
              ) : (
                <div className="min-w-0 flex-1 text-xs italic text-muted-foreground">
                  Not set
                </div>
              )}
              <RolePicker
                role={role}
                contacts={contacts}
                selectedContactId={selectedContact?.id ?? null}
                onSelect={(contactId) => assignMutation.mutate({
                  role: role.roleKey as ContactRole,
                  previousContactId: selectedContact?.id ?? null,
                  newContactId: contactId,
                })}
                disabled={!linkedCompany || assignMutation.isPending}
              />
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
